import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Trash2, Clock, Link, Mail, RefreshCw } from 'lucide-react';
import api from '../api';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard

/**
 * 🎬 项目组邀请弹窗
 * - 显示已有邀请链接
 * - 生成新邀请链接
 * - 复制链接
 * - 删除邀请
 */
export default function CrewInviteModal({ open, onClose, crewId, crewName }) {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [error, setError] = useState('');

    // 邀请有效期选项
    const [expireDays, setExpireDays] = useState(7);

    // 加载邀请列表
    const loadInvitations = React.useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.get(`/api/crews/${crewId}/invitations`);
            if (data.success) {
                setInvitations(data.invitations || []);
            }
        } catch (e) {
            console.error('加载邀请列表失败:', e);
            setError('加载失败');
        } finally {
            setLoading(false);
        }
    }, [crewId]);

    useEffect(() => {
        if (open && crewId) {
            loadInvitations();
        }
    }, [open, crewId, loadInvitations]);

    // 生成新邀请
    const handleGenerate = async () => {
        try {
            setGenerating(true);
            setError('');
            const data = await api.post(`/api/crews/${crewId}/invitations`, {
                expireDays: expireDays
            });
            if (data.success) {
                setInvitations(prev => [data.invitation, ...prev]);
            } else {
                setError(data.error || '生成失败');
            }
        } catch (e) {
            console.error('生成邀请失败:', e);
            setError('生成失败');
        } finally {
            setGenerating(false);
        }
    };

    // 复制邀请链接
    const handleCopy = async (invitation) => {
        const link = `${window.location.origin}/join/${invitation.token}`;
        if (await copyTextToClipboard(link)) {
            setCopiedId(invitation.id);
            setTimeout(() => setCopiedId(null), 2000);
        } else {
            console.error('复制失败');
        }
    };

    // 删除邀请
    const handleDelete = async (invitationId) => {
        try {
            const data = await api.delete(`/api/crews/${crewId}/invitations/${invitationId}`);
            if (data.success) {
                setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
            }
        } catch (e) {
            console.error('删除邀请失败:', e);
        }
    };

    // 格式化过期时间
    const formatExpire = (expiresAt) => {
        if (!expiresAt) return '永久有效';
        const expire = new Date(expiresAt);
        const now = new Date();
        const diff = expire - now;
        if (diff <= 0) return '已过期';
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        if (days > 0) return `${days}天${hours}小时后过期`;
        return `${hours}小时后过期`;
    };

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000
        }} onClick={onClose}>
            <div
                style={{
                    background: 'var(--bg-panel)',
                    borderRadius: 16,
                    border: '1px solid var(--border-subtle)',
                    width: 500,
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* 头部 */}
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid var(--border-subtle)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                            邀请成员加入项目组
                        </h2>
                        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                            {crewName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--text-muted)', cursor: 'pointer', padding: 4
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* 生成新邀请 */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--modal-input-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <select
                            value={expireDays}
                            onChange={e => setExpireDays(Number(e.target.value))}
                            style={{
                                background: 'var(--modal-input-bg)',
                                border: '1px solid var(--border-subtle)',
                                borderRadius: 6, padding: '8px 12px',
                                color: 'var(--text-body)', fontSize: 13,
                                outline: 'none', cursor: 'pointer'
                            }}
                        >
                            <option value={1}>1天有效</option>
                            <option value={3}>3天有效</option>
                            <option value={7}>7天有效</option>
                            <option value={30}>30天有效</option>
                            <option value={0}>永久有效</option>
                        </select>

                        <button
                            onClick={handleGenerate}
                            disabled={generating}
                            style={{
                                flex: 1,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                padding: '10px 16px', borderRadius: 8,
                                background: generating ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.8)',
                                border: 'none',
                                color: '#fff', fontSize: 14, fontWeight: 500,
                                cursor: generating ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => { if(!generating) e.currentTarget.style.background = 'rgba(59,130,246,1)'; }}
                            onMouseLeave={e => { if(!generating) e.currentTarget.style.background = 'rgba(59,130,246,0.8)'; }}
                        >
                            {generating ? (
                                <>
                                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                    生成中...
                                </>
                            ) : (
                                <>
                                    <Link size={16} />
                                    生成邀请链接
                                </>
                            )}
                        </button>
                    </div>

                    {error && (
                        <p style={{ marginTop: 8, fontSize: 13, color: '#f87171' }}>{error}</p>
                    )}
                </div>

                {/* 邀请列表 */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                            加载中...
                        </div>
                    ) : invitations.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                            <Mail size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
                            <p>暂无邀请链接</p>
                            <p style={{ fontSize: 12, marginTop: 4 }}>点击上方按钮生成邀请链接</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {invitations.map(inv => (
                                <div
                                    key={inv.id}
                                    style={{
                                        padding: '12px 14px',
                                        background: inv.used_at ? 'rgba(34,197,94,0.1)' : 'var(--modal-input-bg)',
                                        border: `1px solid ${inv.used_at ? 'rgba(34,197,94,0.2)' : 'var(--modal-input-border)'}`,
                                        borderRadius: 8,
                                        display: 'flex', alignItems: 'center', gap: 12
                                    }}
                                >
                                    {/* 链接信息 */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 13, color: 'var(--text-body)',
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                        }}>
                                            {window.location.origin}/join/{inv.token}
                                        </div>
                                        <div style={{
                                            fontSize: 11, color: 'var(--text-muted)', marginTop: 4,
                                            display: 'flex', alignItems: 'center', gap: 8
                                        }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Clock size={10} />
                                                {inv.used_at ? (
                                                    <span style={{ color: '#22c55e' }}>已使用</span>
                                                ) : (
                                                    formatExpire(inv.expires_at)
                                                )}
                                            </span>
                                            {inv.used_by_username && (
                                                <span style={{ color: '#22c55e' }}>
                                                    • {inv.used_by_username} 已加入
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 操作按钮 */}
                                    {!inv.used_at && (
                                        <>
                                            <button
                                                onClick={() => handleCopy(inv)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 32, height: 32, borderRadius: 6,
                                                    background: copiedId === inv.id ? 'rgba(34,197,94,0.2)' : 'var(--modal-input-bg)',
                                                    border: 'none',
                                                    color: copiedId === inv.id ? '#22c55e' : 'var(--text-secondary)',
                                                    cursor: 'pointer', transition: 'all 0.2s'
                                                }}
                                                title="复制链接"
                                            >
                                                {copiedId === inv.id ? <Check size={16} /> : <Copy size={16} />}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(inv.id)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 32, height: 32, borderRadius: 6,
                                                    background: 'var(--modal-input-bg)',
                                                    border: 'none',
                                                    color: 'var(--text-secondary)',
                                                    cursor: 'pointer', transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.color = '#f87171'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = 'var(--modal-input-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                                                title="删除邀请"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 底部提示 */}
                <div style={{
                    padding: '12px 24px',
                    background: 'rgba(251,191,36,0.05)',
                    borderTop: '1px solid var(--modal-input-border)',
                    fontSize: 12, color: 'var(--text-secondary)'
                }}>
                    💡 受邀成员将以"设计师"身份加入项目组，可以查看和添加素材，但不能删除公共素材库中的内容
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
